import React, { useState } from 'react';
import { CheckCircle2, ShieldCheck, CreditCard, Clock, Star } from 'lucide-react';
import { motion } from 'framer-motion';
const PLANS = {
  base: {
    name: 'Autonomía Total',
    subtitle: '6 tomos + 6 bonos clínicos',
    oldPrice: '$34.990',
    price: '$19.990',
    cta: 'QUIERO MI MEGAPACK',
    url: 'https://checkout.mundodigitalarg.com/cart/48219661664497:1',
    features: ['6 Tomos completos', '6 Bonos clínicos incluidos']
  },
  pro: {
    name: 'Autonomía Total PRO',
    subtitle: '6 tomos + 6 bonos + PRO 1 y 2',
    oldPrice: '$43.990',
    price: '$29.990',
    cta: 'QUIERO EL MEGAPACK PRO',
    url: 'https://checkout.mundodigitalarg.com/cart/48219661697265:1',
    features: ['Todo lo del Base', 'PRO 1: Abordaje Interdisciplinario', 'PRO 2: Gestión Profesional']
  }
} as const;
export const Pricing = () => {
  const [selected, setSelected] = useState<'base' | 'pro'>('pro');
  return <section id="pricing" className="py-12 md:py-20 bg-white scroll-mt-16" data-id="element-202">
      <div className="max-w-3xl mx-auto px-4" data-id="element-203">
        <div className="text-center mb-6 md:mb-8" data-id="element-204">
          <h2 className="text-2xl md:text-4xl font-extrabold text-navy mb-2 leading-tight" data-id="element-205">
            Elegí tu Megapack
          </h2>
          <p className="text-sm md:text-base text-gray-600" data-id="element-206">
            Ambas opciones incluyen los 6 bonos clínicos
          </p>
        </div>

        <div className="flex flex-col gap-3 md:gap-4 mb-6" data-id="element-207">
          {/* PRO Plan - on top because preselected/recommended */}
          <button type="button" onClick={() => setSelected('pro')} className={`relative text-left rounded-2xl p-4 md:p-5 transition-all border-2 ${selected === 'pro' ? 'border-gold bg-navy text-white shadow-2xl shadow-gold/20' : 'border-gray-300 bg-white text-navy hover:border-gold/50'}`} aria-pressed={selected === 'pro'} data-id="element-208">
            {/* MÁS ELEGIDA badge */}
            <div className={`absolute -top-2.5 left-4 md:left-6 font-black px-2.5 md:px-3 py-1 rounded-full text-[9px] md:text-xs whitespace-nowrap shadow-lg flex items-center gap-1 ${selected === 'pro' ? 'bg-gold text-navy' : 'bg-gray-100 text-gray-500'}`} data-id="element-209">
              <Star className="w-2.5 h-2.5 md:w-3 md:h-3 fill-current" data-id="element-210" /> MÁS
              ELEGIDA
            </div>

            <div className="flex items-center gap-3 md:gap-4" data-id="element-211">
              {/* Radio */}
              <div className={`flex-shrink-0 w-6 h-6 md:w-7 md:h-7 rounded-full border-2 flex items-center justify-center transition-colors ${selected === 'pro' ? 'border-gold bg-gold' : 'border-gray-300 bg-white'}`} data-id="element-212">
                {selected === 'pro' && <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-navy" data-id="element-213" />}
              </div>

              {/* Title + subtitle */}
              <div className="flex-grow min-w-0" data-id="element-214">
                <h3 className={`text-sm md:text-lg font-bold leading-tight ${selected === 'pro' ? 'text-white' : 'text-navy'}`} data-id="element-215">
                  {PLANS.pro.name}
                </h3>
                <p className={`text-[11px] md:text-sm font-medium ${selected === 'pro' ? 'text-gold' : 'text-gray-500'}`} data-id="element-216">
                  {PLANS.pro.subtitle}
                </p>
              </div>

              {/* Price */}
              <div className="flex-shrink-0 text-right" data-id="element-217">
                <div className={`line-through text-[11px] md:text-sm font-bold ${selected === 'pro' ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-218">
                  {PLANS.pro.oldPrice}
                </div>
                <div className={`text-xl md:text-3xl font-black leading-none ${selected === 'pro' ? 'text-white' : 'text-navy'}`} data-id="element-219">
                  {PLANS.pro.price}
                </div>
              </div>
            </div>

            {/* Features - only show when selected */}
            {selected === 'pro' && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-x-3 gap-y-1" data-id="element-220">
                {PLANS.pro.features.map((f, i) => <div key={i} className="flex items-center gap-1 text-[10px] md:text-xs text-gray-200 font-medium" data-id="element-221">
                    <CheckCircle2 className="w-3 h-3 md:w-3.5 md:h-3.5 flex-shrink-0 text-gold" data-id="element-222" />
                    <span data-id="element-223">{f}</span>
                  </div>)}
              </motion.div>}
          </button>

          {/* Base Plan */}
          <button type="button" onClick={() => setSelected('base')} className={`relative text-left rounded-2xl p-4 md:p-5 transition-all border-2 ${selected === 'base' ? 'border-navy bg-navy text-white shadow-xl' : 'border-gray-200 bg-cream text-navy hover:border-gray-300'}`} aria-pressed={selected === 'base'} data-id="element-224">
            <div className="flex items-center gap-3 md:gap-4" data-id="element-225">
              <div className={`flex-shrink-0 w-6 h-6 md:w-7 md:h-7 rounded-full border-2 flex items-center justify-center transition-colors ${selected === 'base' ? 'border-gold bg-gold' : 'border-gray-300 bg-white'}`} data-id="element-226">
                {selected === 'base' && <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-navy" data-id="element-227" />}
              </div>

              <div className="flex-grow min-w-0" data-id="element-228">
                <h3 className={`text-sm md:text-lg font-bold leading-tight ${selected === 'base' ? 'text-white' : 'text-navy'}`} data-id="element-229">
                  {PLANS.base.name}
                </h3>
                <p className={`text-[11px] md:text-sm font-medium ${selected === 'base' ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-230">
                  {PLANS.base.subtitle}
                </p>
              </div>

              <div className="flex-shrink-0 text-right" data-id="element-231">
                <div className={`line-through text-[11px] md:text-sm font-bold ${selected === 'base' ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-232">
                  {PLANS.base.oldPrice}
                </div>
                <div className={`text-xl md:text-3xl font-black leading-none ${selected === 'base' ? 'text-white' : 'text-navy'}`} data-id="element-233">
                  {PLANS.base.price}
                </div>
              </div>
            </div>

            {selected === 'base' && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-x-3 gap-y-1" data-id="element-234">
                {PLANS.base.features.map((f, i) => <div key={i} className="flex items-center gap-1 text-[10px] md:text-xs text-gray-300 font-medium" data-id="element-235">
                    <CheckCircle2 className="w-3 h-3 md:w-3.5 md:h-3.5 flex-shrink-0 text-gold" data-id="element-236" />
                    <span data-id="element-237">{f}</span>
                  </div>)}
              </motion.div>}
          </button>
        </div>

        {/* Shared CTA */}
        <motion.a key={selected} initial={{
        scale: 0.98,
        opacity: 0.8
      }} animate={{
        scale: 1,
        opacity: 1
      }} href={PLANS[selected].url} target="_blank" rel="noopener noreferrer" className="block w-full text-center bg-gold hover:bg-gold-light text-navy font-black py-4 md:py-5 rounded-xl transition-colors shadow-[0_0_25px_rgba(212,162,76,0.4)] active:scale-[0.98] text-base md:text-lg" data-id="element-238">
          💥 {PLANS[selected].cta} · {PLANS[selected].price}
        </motion.a>

        <div className="flex flex-wrap justify-center items-center gap-3 md:gap-6 mt-6 md:mt-8 text-[11px] md:text-sm text-gray-500 font-medium" data-id="element-239">
          <div className="flex items-center gap-1.5" data-id="element-240">
            <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-241" /> Pago seguro
          </div>
          <div className="flex items-center gap-1.5" data-id="element-242">
            <Clock className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-243" /> Acceso en 5 min
          </div>
          <div className="flex items-center gap-1.5" data-id="element-244">
            <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-245" /> Garantía 7
            días
          </div>
          <div className="flex items-center gap-1.5" data-id="element-246">
            <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4" data-id="element-247" /> Mercado Pago
          </div>
        </div>
      </div>
    </section>;
};